import { nextTick, onMounted, toValue, type MaybeRefOrGetter } from 'vue'
import { usePipe } from '@render/composables/usePipe'
import { parseNumericVar } from '@render/lib/parseNumericVar'

/**
 * Seed an input's value from another variable once, on mount, only when the
 * input is still empty. Used to pre-fill a field with a computed default (e.g.
 * pre-fill the goal-weight input from the Fitness Functions integration's
 * `global.suggestedTargetWeightKg`, itself derived from the user's height).
 *
 * Rules that make it safe:
 * - `await pipe.whenReady()` waits for the boot-time localStorage->variables
 *   merge, so a returning user's saved value is visible before we decide.
 * - `hasValue` is checked after that merge: if the input already holds a value,
 *   we never overwrite it (seed-when-empty).
 * - `await nextTick()` lets the source variable's producer flush first. The
 *   Fitness Functions watch that derives the source from height runs on the
 *   default `pre` flush, so without this tick the source could still be empty
 *   the instant `whenReady()` resolves.
 * - One-shot: it runs once on mount. If the source isn't computed yet at that
 *   point (e.g. height entered on the same page below this input), it skips
 *   rather than reactively re-seeding, which avoids re-seeding a field the user
 *   deliberately cleared.
 *
 * Numeric-only: parses via `parseNumericVar`.
 */
export interface UsePrefillFromVariableOptions {
  /** Bare source variable key to read the prefill value from (no `var.` prefix). */
  sourceKey: MaybeRefOrGetter<string | undefined>
  /** True when the input already holds a value, so prefill is skipped. */
  hasValue: MaybeRefOrGetter<boolean>
  /** Writes the seeded value into the input's own variable (transiently). */
  seed: (value: number) => void
}

export function usePrefillFromVariable(options: UsePrefillFromVariableOptions): void {
  const pipe = usePipe()

  onMounted(async () => {
    await pipe.whenReady()
    await nextTick()

    if (toValue(options.hasValue)) return
    const key = toValue(options.sourceKey)
    if (!key) return
    const value = parseNumericVar(pipe.getData(`var.${key}`))
    if (value === undefined) return
    options.seed(value)
  })
}
